import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { 
  Brain, Target, BookOpen, MessageCircle, 
  Flame, Clock, TrendingUp, ChevronRight, Sparkles 
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function Dashboard() {
  const { user, getAuthHeader, API } = useAuth();
  const { t, i18n } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [affirmation, setAffirmation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [i18n.language]);

  const fetchDashboardData = async () => {
    const lang = i18n.language === 'no' ? 'no' : 'en';
    try {
      const [analyticsRes, affirmationsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { headers: getAuthHeader() }),
        axios.get(`${API}/affirmations?lang=${lang}`, { headers: getAuthHeader() })
      ]);
      setAnalytics(analyticsRes.data);
      setAffirmation(affirmationsRes.data[0]);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      athlete: t('auth.categories.athlete'),
      golfer: t('auth.categories.golfer'),
      business_leader: t('auth.categories.business_leader')
    };
    return labels[category] || 'Champion';
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    const lang = i18n.language;
    if (lang === 'no') {
      if (hour < 12) return 'God morgen';
      if (hour < 18) return 'God ettermiddag';
      return 'God kveld';
    }
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div>
            <p className="font-mono text-xs tracking-widest uppercase text-primary/80 mb-1">
              {getCategoryLabel(user?.category)}
            </p>
            <h1 className="font-serif font-bold text-3xl md:text-4xl text-foreground">
              {getGreeting()}, {user?.name?.split(' ')[0]}
            </h1>
          </div>
          <Link to="/coach">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6" data-testid="talk-to-coach-btn">
              <MessageCircle className="mr-2 h-4 w-4" />
              {t('dashboard.actions.talk_to_coach')}
            </Button>
          </Link>
        </motion.div>

        {/* Daily Affirmation */}
        {affirmation && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="glass neon-border overflow-hidden">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-primary/20">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-mono text-xs tracking-widest uppercase text-primary/80 mb-2">
                      {t('dashboard.daily_affirmation')}
                    </p>
                    <p className="font-serif text-xl md:text-2xl text-foreground italic" data-testid="daily-affirmation">
                      "{affirmation.text}"
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Stats Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Streak Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-card/50 border-white/5 hover:border-primary/30 transition-all duration-500 h-full">
              <CardContent className="p-6 flex flex-col justify-between h-full">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 rounded-lg bg-orange-500/20">
                    <Flame className="h-5 w-5 text-orange-500" />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{i18n.language === 'no' ? 'REKKE' : 'STREAK'}</span>
                </div>
                <div>
                  <p className="font-serif text-4xl font-bold text-foreground" data-testid="streak-count">
                    {loading ? '-' : analytics?.streak || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">{i18n.language === 'no' ? 'dager' : 'days'}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Sessions Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Card className="bg-card/50 border-white/5 hover:border-primary/30 transition-all duration-500 h-full">
              <CardContent className="p-6 flex flex-col justify-between h-full">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 rounded-lg bg-secondary/20">
                    <Brain className="h-5 w-5 text-secondary" />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{i18n.language === 'no' ? 'ØKTER' : 'SESSIONS'}</span>
                </div>
                <div>
                  <p className="font-serif text-4xl font-bold text-foreground" data-testid="session-count">
                    {loading ? '-' : analytics?.meditation_sessions || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">{i18n.language === 'no' ? 'fullført' : 'completed'}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Minutes Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-card/50 border-white/5 hover:border-primary/30 transition-all duration-500 h-full">
              <CardContent className="p-6 flex flex-col justify-between h-full">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 rounded-lg bg-primary/20">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{i18n.language === 'no' ? 'MINUTTER' : 'MINUTES'}</span>
                </div>
                <div>
                  <p className="font-serif text-4xl font-bold text-foreground" data-testid="minutes-count">
                    {loading ? '-' : analytics?.total_meditation_minutes || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">{i18n.language === 'no' ? 'trent' : 'trained'}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Goals Progress Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <Card className="bg-card/50 border-white/5 hover:border-primary/30 transition-all duration-500 h-full">
              <CardContent className="p-6 flex flex-col justify-between h-full">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 rounded-lg bg-accent/20">
                    <Target className="h-5 w-5 text-accent" />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{i18n.language === 'no' ? 'MÅL' : 'GOALS'}</span>
                </div>
                <div>
                  <p className="font-serif text-4xl font-bold text-foreground" data-testid="goals-completed">
                    {loading ? '-' : `${analytics?.completed_goals || 0}/${analytics?.total_goals || 0}`}
                  </p>
                  <p className="text-sm text-muted-foreground">{i18n.language === 'no' ? 'oppnådd' : 'achieved'}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="font-serif font-semibold text-xl text-foreground mb-4">{t('dashboard.quick_actions')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/meditations" data-testid="quick-action-meditations">
              <Card className="group bg-card/50 border-white/5 hover:border-secondary/30 transition-all duration-300 cursor-pointer hover-lift">
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-secondary/20 group-hover:bg-secondary/30 transition-colors">
                      <Brain className="h-6 w-6 text-secondary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{t('dashboard.actions.start_meditation')}</p>
                      <p className="text-sm text-muted-foreground">{t('nav.meditations')}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-secondary transition-colors" />
                </CardContent>
              </Card>
            </Link>

            <Link to="/journal" data-testid="quick-action-journal">
              <Card className="group bg-card/50 border-white/5 hover:border-accent/30 transition-all duration-300 cursor-pointer hover-lift">
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-accent/20 group-hover:bg-accent/30 transition-colors">
                      <BookOpen className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{t('dashboard.actions.write_journal')}</p>
                      <p className="text-sm text-muted-foreground">{t('nav.journal')}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors" />
                </CardContent>
              </Card>
            </Link>

            <Link to="/goals" data-testid="quick-action-goals">
              <Card className="group bg-card/50 border-white/5 hover:border-primary/30 transition-all duration-300 cursor-pointer hover-lift">
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-primary/20 group-hover:bg-primary/30 transition-colors">
                      <Target className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{t('dashboard.actions.set_goal')}</p>
                      <p className="text-sm text-muted-foreground">{t('nav.goals')}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </CardContent>
              </Card>
            </Link>

            <Link to="/analytics" data-testid="quick-action-analytics">
              <Card className="group bg-card/50 border-white/5 hover:border-chart-5/30 transition-all duration-300 cursor-pointer hover-lift">
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-chart-5/20 group-hover:bg-chart-5/30 transition-colors">
                      <TrendingUp className="h-6 w-6 text-chart-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{i18n.language === 'no' ? 'Se Fremgang' : 'View Progress'}</p>
                      <p className="text-sm text-muted-foreground">{t('nav.analytics')}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-chart-5 transition-colors" />
                </CardContent>
              </Card>
            </Link>
          </div>
        </motion.div>

        {/* Goals Progress Section */}
        {analytics && analytics.total_goals > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="bg-card/50 border-white/5">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  {i18n.language === 'no' ? 'Målfremgang' : 'Goals Progress'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Completion Rate</span>
                    <span className="text-foreground font-semibold">{analytics.goals_completion_rate}%</span>
                  </div>
                  <Progress value={analytics.goals_completion_rate} className="h-2" />
                  <p className="text-sm text-muted-foreground">
                    {analytics.completed_goals} of {analytics.total_goals} goals completed
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </Layout>
  );
}
